import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NumericFormat } from "react-number-format";


interface MicroTarget {
  ftd: number;
  profit: number;
  nrpu: number;
  spend: number;
  clicks: number;
  cvr: number;
  cac: number;
  cpa: number;
  cpc: number;
  rs: number;
  total_income: number;
  cpa_pv: number;
  rs_pv: number;
  pv: number;
  net_revenue: number;
  avg_rs_deal: number;
  epc: number;
  epc_cpa: number;
  epc_rs: number;
  geo: string;
  product_type: string;
  website_target_shared_uid: string;
  shared_uid: string;
}

interface EditMicroTargetsProps {
  websiteMicroTarget: any;
  selectedTargetSharedUid: string;
  GeoList: any[];
  ProductTypes: any[];
  onMicroTargetChange: (index: number, field: string, value: any) => void;
  microTargetPayload: any;
}
export interface EditMicroTargetsRef {
  updateMicroTargets: () => Promise<void>;
}

const EditMicroTargets = ({
  websiteMicroTarget,
  GeoList,
  ProductTypes,
  onMicroTargetChange,
  microTargetPayload
}: EditMicroTargetsProps) => {
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
  const findOptionByName = (options: any[], name: string) => {
    return options.find((option) => option.name === name) || null;
  };

  const handlePanelChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanels((prev) =>
        isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
      );
    };

  return (
    <>
      {websiteMicroTarget.map((microTarget: MicroTarget, index: number) => {
        const currentPayload = microTargetPayload[index] || microTarget;
        return (
          <Grid2 container columnSpacing={0} key={microTarget.shared_uid}>
            <Grid2 size={12} mb={2}>
              <Accordion
                expanded={expandedPanels.includes(`panel${index}`)}
                onChange={handlePanelChange(`panel${index}`)}
                className="AddMicroTargetWrap"
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color="secondary" />}
                  aria-controls={`panel${index}bh-content`}
                  id={`panel${index}bh-header`}
                >
                  <Typography
                    component="span"
                    sx={{ width: "33%", flexShrink: 0 }}
                  >
                    Micro - Target #{index + 1}
                  </Typography>
                  <Typography component="span" sx={{ color: "text.secondary" }}>
                    {currentPayload.geo} - {currentPayload.product_type}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid2 container mt={1} columnSpacing={3} mb={1}>
                    <Grid2 size={12}>
                      <Typography fontSize={16} mb={1}>
                        General:
                      </Typography>
                    </Grid2>
                    <Grid2 size={4}>
                      <Autocomplete
                        sx={{ width: "100%" }}
                        options={GeoList}
                        getOptionLabel={(option: any) => option?.name || ""}
                        value={
                          findOptionByName(GeoList, currentPayload.geo) || null
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.name === value?.name
                        }
                        onChange={(_, newValue) => {
                          onMicroTargetChange(
                            index,
                            "geo",
                            newValue?.name || ""
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Geo"
                            label="Geo"
                            required
                            InputProps={{
                              ...params.InputProps,
                              readOnly: true,
                            }}
                            color="secondary"
                          />
                        )}
                        size="small"
                        freeSolo={false}
                        disableClearable
                      />
                    </Grid2>
                    <Grid2 size={4}>
                      <Autocomplete
                        sx={{ width: "100%" }}
                        options={ProductTypes}
                        getOptionLabel={(option: any) => option?.name || ""}
                        value={
                          findOptionByName(
                            ProductTypes,
                            currentPayload.product_type
                          ) || null
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.name === value?.name
                        }
                        onChange={(_, newValue) => {
                          onMicroTargetChange(
                            index,
                            "product_type",
                            newValue?.name || ""
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Product"
                            label="Product"
                            required
                            InputProps={{
                              ...params.InputProps,
                              readOnly: true,
                            }}
                            color="secondary"
                          />
                        )}
                        size="small"
                        freeSolo={false}
                        disableClearable
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={3} columnSpacing={3}>
                    <Grid2 size={12}>
                      <Typography fontSize={16} mb={1}>
                        Key metrics:
                      </Typography>
                    </Grid2>
                    <Grid2 size={4}>
                      <NumericFormat
                        customInput={TextField}
                        label="FTD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.ftd}
                        onValueChange={(values) =>
                          onMicroTargetChange(index, "ftd", values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                      />
                    </Grid2>
                    <Grid2 size={4}>
                      <NumericFormat
                        customInput={TextField}
                        label="Profit"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.profit}
                        onValueChange={(values) =>
                          onMicroTargetChange(index, "profit", values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4}>
                      <NumericFormat
                        customInput={TextField}
                        label="NRPU"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.nrpu}
                        onValueChange={(values) =>
                          onMicroTargetChange(index, "nrpu", values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="Spend"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.spend}
                        onValueChange={(values) =>
                          onMicroTargetChange(index, "spend", values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="Clicks"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.clicks}
                        onValueChange={(values) =>
                          onMicroTargetChange(index, "clicks", values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="CVR"
                        placeholder="In Percentage"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.cvr}
                        onValueChange={(values) =>
                            onMicroTargetChange(index, "cvr", values.value)
                        }
                        isAllowed={(values) => {
                          const { formattedValue } = values;
                          // Allow digits, commas, and one decimal point
                          return /^[\d,]*\.?\d*$/.test(
                            formattedValue.replace(/,/g, "")
                          );
                        }}
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="CAC"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.cac}
                        onValueChange={(values) =>
                          onMicroTargetChange(index, "cac", values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">$</InputAdornment>,
                        }}
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={3} columnSpacing={3}>
                    <Grid2 size={12}>
                      <Typography fontSize={16} mb={1}>
                        Income:
                      </Typography>
                    </Grid2>
                    <Grid2 size={4}>
                      <NumericFormat
                        customInput={TextField}
                        label="CPA"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.cpa}
                        onValueChange={(values) =>
                          onMicroTargetChange(index, "cpa", values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4}>
                      <NumericFormat
                        customInput={TextField}
                        label="RS"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.rs}
                        onValueChange={(values) =>
                          onMicroTargetChange(index, "rs", values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4}>
                      <NumericFormat
                        customInput={TextField}
                        label="Total Income"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.total_income}
                        onValueChange={(values) =>
                          onMicroTargetChange(index,"total_income",values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="CPA PV"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.cpa_pv}
                        onValueChange={(values) =>
                          onMicroTargetChange(index, "cpa_pv", values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="RS PV"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.rs_pv}
                        onValueChange={(values) =>
                          onMicroTargetChange(index, "rs_pv", values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="PV"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.pv}
                        onValueChange={(values) =>
                          onMicroTargetChange(index, "pv", values.value)
                        }
                        thousandSeparator=","
                        decimalScale={2} // optional: limits to 2 decimal places
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    {/* <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="PV"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.cpc}
                        onChange={(e) =>
                          onMicroTargetChange(index, "cpc", e.target.value)
                        }
                        thousandSeparator=","
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="Net Revenue"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.net_revenue}
                        onChange={(e) =>
                          onMicroTargetChange(
                            index,
                            "net_revenue",
                            e.target.value
                          )
                        }
                        thousandSeparator=","
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="Avg RS Deal"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.avg_rs_deal}
                        onChange={(e) =>
                          onMicroTargetChange(
                            index,
                            "avg_rs_deal",
                            e.target.value
                          )
                        }
                        thousandSeparator=","
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="EPC"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.epc}
                        onChange={(e) =>
                          onMicroTargetChange(index, "epc", e.target.value)
                        }
                        thousandSeparator=","
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="EPC CPA"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.epc_cpa}
                        onChange={(e) =>
                          onMicroTargetChange(index, "epc_cpa", e.target.value)
                        }
                        thousandSeparator=","
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2>
                    <Grid2 size={4} mt={2}>
                      <NumericFormat
                        customInput={TextField}
                        label="EPC RS"
                        placeholder="In USD"
                        variant="outlined"
                        fullWidth
                        color="secondary"
                        size="small"
                        value={currentPayload.epc_rs}
                        onChange={(e) =>
                          onMicroTargetChange(index, "epc_rs", e.target.value)
                        }
                        thousandSeparator=","
                        sx={{ width: "100%" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid2> */}
                  </Grid2>
                </AccordionDetails>
              </Accordion>
            </Grid2>
          </Grid2>
        );
      })}
    </>
  );
};

export default EditMicroTargets;
